import { createStore } from 'zustand';
import {
  UploadApiError,
  fetchStudyImages,
  patchImage,
  patchImageStudy,
  patchStudyDetails,
  type CaseDetails,
  type EnterpriseImage,
  type EnterpriseImageDetails,
  type ImageStage,
} from '../../features/upload/api';
import { getStudyRef, type StudyRef } from '../../features/collections/api';
import { withRetry } from '../../features/upload/transfer';
import { certainKind, type ImageKind } from '../../data/imageTypes';
import { createDetailsEditorRuntime } from '../../features/details/editor';
import { refreshDetailsViews } from '../../features/details/freshness';
import { EMPTY_DRAFT, type MetadataDraft } from '../../features/upload/sidecar';
import { queryClient } from '../../hooks/queryClient';
import {
  adoptView,
  deltaOf,
  draftFromView,
  imageBody,
  kindOf,
  studyBody,
  studyDateTimeField,
  studyGroupFields,
  wireOf,
  type FieldProblem,
  type FileKind,
  type Wire,
} from '../upload/details';
import { POLL_MS, detailsErrorOf } from '../upload/store';

/**
 * The details editor of a study that is already in the worklist (enterprise):
 * the case (patient, accession, date, physician, description, diagnosis, sex,
 * age) once, and each image's own facts, saved as they are typed over the
 * routes the upload page uses: PATCH /studies/:id/details for the case,
 * PATCH /images/:id for an image, and PATCH /images/:id/study to move ONE
 * image into another case.
 *
 * Every save is a DELTA of GROUPS against what the server last said
 * (upload/details.ts): a group the person did not touch is never sent, so what
 * the LIS or ingest wrote, or another person changed, is not overwritten by a
 * form that merely still shows the old value. The case's accession is the one
 * field saved only when it loses focus: renaming a case one keystroke at a time
 * would pass through other cases' numbers.
 */

/** The study head as the editor reads it (GET /studies/:studyId, a member in the enterprise edition). */
export interface CaseHead extends StudyRef {
  study_id: string | null;
  description: string | null;
  diagnosis?: string | null;
  sex?: 'M' | 'F' | 'O' | null;
  /** DICOM AS, e.g. `054Y`. */
  age?: string | null;
  /** `YYYY-MM-DD`. */
  study_date?: string | null;
  /** DICOM TM, `HHMMSS`. */
  study_time?: string | null;
  accession_number?: string | null;
  accession_issuer?: string | null;
  /** DICOM PN, `Family^Given`. */
  referring_physician?: string | null;
  patient_id?: string | null;
  patient_issuer?: string | null;
  patient_name?: string | null;
  patient_birth_date?: string | null;
}

/** A refused save, and the field it is about: a form field, or the case's diagnosis (not part of the upload form). */
export interface EditorError {
  field: keyof MetadataDraft | 'diagnosis' | null;
  message: string;
}

/** The case, as the form holds it. */
export interface CaseForm {
  draft: MetadataDraft;
  diagnosis: string;
}
/** The case as the server last took it: the study groups, and the diagnosis. */
export interface CaseSaved {
  study: Wire['study'];
  diagnosis: string | null;
}

export interface ImageRow {
  imageId: string;
  name: string;
  kind: FileKind;
  /** What the file certainly is (the converter's word, or a scanner's format), or null: the Types it cannot be are greyed. */
  certain: ImageKind | null;
  stage: ImageStage;
  percent: number | null;
  stageError: string | null;
  /** The form (its image fields are the ones drawn). */
  draft: MetadataDraft;
  /** The image groups as the server last took them. */
  saved: Wire['image'];
  /** The server could say only the image's summary: a blank advanced field may hold a value there. */
  partial: boolean;
  /** The form fields the person changed: only their problems hold a save back (touchedProblems). */
  touched: ReadonlyArray<keyof MetadataDraft>;
  error: EditorError | null;
  /** "Move to another case": the accession typed, the call running, and why it did not happen. */
  moveTo: { accession: string; issuer: string };
  moving: boolean;
  moveError: string | null;
}

/** An image that left this study for another case. */
export interface Moved {
  name: string;
  accession: string;
  studyId: string;
}

export const CASE = 'case';
/** The enterprise pages outside DICOMweb that show a study's details: the collection list and a collection's studies. */
const ENTERPRISE_CATALOGUE = ['collections', 'collection'];
const SAVE_DEBOUNCE_MS = 600;
const CALL_ATTEMPTS = 4;

/** The form fields of the case: what the case part of the form draws. */
export const CASE_FIELDS: ReadonlyArray<keyof MetadataDraft> = [
  'patientFamily', 'patientGiven', 'patientId', 'patientBirthDate', 'patientSex', 'patientAge', 'accession', 'studyDateTime', 'studyDescription',
  'referringPhysician', 'patientIdIssuer', 'accessionIssuer', 'studyId',
];
const ACCESSION_FIELDS: ReadonlyArray<keyof MetadataDraft> = ['accession', 'accessionIssuer'];

/** Published, or processed and waiting to be confirmed: an image still being uploaded or converted is shown and not edited. */
export const isEditable = (r: Pick<ImageRow, 'stage'>) => r.stage === 'published' || r.stage === 'needs-details';
export const isProcessing = (r: Pick<ImageRow, 'stage'>) => !isEditable(r) && r.stage !== 'failed';

/** The case form, from the head: the inverse of the case body, group by group. */
export function caseFormOf(h: CaseHead): CaseForm {
  const draft: MetadataDraft = {
    ...EMPTY_DRAFT,
    ...studyGroupFields('study_id', h.study_id),
    ...studyGroupFields('description', h.description),
    ...studyGroupFields('sex', h.sex ?? null),
    ...studyGroupFields('age', h.age ?? null),
    ...studyGroupFields('referring_physician', h.referring_physician ?? null),
    ...studyGroupFields('accession', h.accession_number ? { number: h.accession_number, issuer: h.accession_issuer ?? null } : null),
    ...studyGroupFields('patient', h.patient_id ? { id: h.patient_id, issuer: h.patient_issuer ?? null, name: h.patient_name ?? null, birth_date: h.patient_birth_date ?? null } : null),
    studyDateTime: studyDateTimeField(h.study_date ?? null, h.study_time ?? null),
  };
  return { draft, diagnosis: h.diagnosis ?? '' };
}

/** What the server took, measured the way a save is: the form's own groups. */
export const caseSavedOf = (f: CaseForm): CaseSaved => ({ study: wireOf(f.draft).wire.study, diagnosis: f.diagnosis.trim() || null });

/** What in the case form the server would refuse, said at its field before anything is sent. */
export const caseProblems = (f: CaseForm): FieldProblem[] => wireOf(f.draft).problems.filter((p) => CASE_FIELDS.includes(p.field));

/**
 * Of a form's problems, the ones in fields the person changed. A value the
 * LIS or an older import stored in a shape the form would not accept is not
 * the person's to fix before they may leave: it is shown only once edited.
 */
export const touchedProblems = (problems: FieldProblem[], touched: ReadonlyArray<keyof MetadataDraft>): FieldProblem[] =>
  problems.filter((p) => touched.includes(p.field));

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * What a case save changes: the groups that differ from what the server last
 * took (a group the server would refuse is left out by wireOf), the diagnosis
 * when it changed, and the body that says so. `accession: false` holds the
 * accession back while it is being typed.
 */
export function caseChanges(f: CaseForm, saved: CaseSaved, opts: { accession?: boolean } = {}): { groups: Wire['study']; diagnosis?: string | null; body: CaseDetails } {
  const groups = deltaOf({ image: {}, study: wireOf(f.draft).wire.study }, { image: {}, study: saved.study }).study;
  if (opts.accession === false) delete groups.accession;
  const body = studyBody(groups);
  const diagnosis = f.diagnosis.trim() || null;
  if (diagnosis === saved.diagnosis) return { groups, body };
  return { groups, diagnosis, body: { ...body, diagnosis } };
}
export const caseDelta = (f: CaseForm, saved: CaseSaved, opts: { accession?: boolean } = {}): CaseDetails => caseChanges(f, saved, opts).body;

/** An image's row, from the server's view of it. */
export function rowOf(v: EnterpriseImage): ImageRow {
  const name = v.name ?? v.image_id;
  const { draft, saved, partial } = draftFromView(v);
  // The kind is the converter's word once it has spoken; before that the file name's. Only a scanner's
  // format limits the Type: how the conversion stored a picture never does.
  const spoken = isEditable(v) && (v.kind === 'wsi' || v.kind === 'flat' || v.kind === 'document') ? v.kind : null;
  const kind: FileKind = spoken ?? kindOf(name);
  return {
    imageId: v.image_id, name, kind, certain: certainKind(name), stage: v.stage, percent: v.percent ?? null, stageError: v.error, draft, saved: saved.image, partial, touched: [], error: null,
    moveTo: { accession: '', issuer: '' }, moving: false, moveError: null,
  };
}

/** What an image save sends: its groups that differ from what the server last took. */
export function imageDelta(r: Pick<ImageRow, 'draft' | 'saved'>): EnterpriseImageDetails {
  return imageBody(deltaOf({ image: wireOf(r.draft).wire.image, study: {} }, { image: r.saved, study: {} }).image);
}

/** What the server now says about an image, taken where the row has nothing of its own to say (adoptView's rule). */
export function adoptImage(r: ImageRow, v: EnterpriseImage): ImageRow {
  const stage = { stage: v.stage, percent: v.percent ?? null, stageError: v.error };
  if (!isEditable(r)) return { ...rowOf(v), moveTo: r.moveTo, touched: r.touched, error: r.error };
  const adopted = adoptView(r.draft, { image: r.saved, study: wireOf(r.draft).wire.study }, v);
  return adopted ? { ...r, ...stage, draft: adopted.draft, saved: adopted.saved.image } : { ...r, ...stage };
}

/** What a refused save says, and its field. `part` is the body that was sent. */
export function refusalOf(err: unknown, part: 'image' | 'study'): EditorError {
  if (err instanceof UploadApiError) {
    if (part === 'study' && err.status === 400 && err.message === 'invalid diagnosis') return { field: 'diagnosis', message: 'The server did not accept this diagnosis.' };
    if (err.status === 403) return { field: null, message: err.message && err.message !== '403' ? err.message.charAt(0).toUpperCase() + err.message.slice(1) : 'You can no longer edit this study.' };
    if (err.status === 404) return { field: null, message: part === 'study' ? 'This study no longer exists. It may have been deleted.' : 'The server no longer has this image. It may have been removed.' };
  }
  return detailsErrorOf(err, part);
}

export interface EditorState {
  studyId: string;
  caseForm: CaseForm;
  caseSaved: CaseSaved;
  caseError: EditorError | null;
  /** The case form fields the person changed. */
  caseTouched: ReadonlyArray<keyof MetadataDraft>;
  images: ImageRow[] | null;
  imagesError: string | null;
  selectedId: string | null;
  pending: boolean;
  saved: boolean;
  /** The images that left for another case while the editor was open. */
  moved: Moved[];
  open: (head: CaseHead) => void;
  takeHead: (head: CaseHead) => void;
  setCase: (p: Partial<MetadataDraft>) => void;
  setDiagnosis: (v: string) => void;
  setImage: (imageId: string, p: Partial<MetadataDraft>) => void;
  select: (imageId: string) => void;
  setMoveTo: (imageId: string, p: Partial<ImageRow['moveTo']>) => void;
  /** Move one image into the case its accession names (PATCH /images/:id/study). */
  move: (imageId: string) => Promise<void>;
  flush: (key?: string) => Promise<void>;
  sync: (images: ReadonlyArray<EnterpriseImage>) => void;
  close: () => void;
}

/**
 * How many targets hold a change the server has not taken and will not take as
 * it is: refused by the server, or a problem the form shows at a field. Done
 * stops for these; a change merely waiting is sent by Done itself.
 */
export function refusedCount(s: Pick<EditorState, 'caseForm' | 'caseError' | 'caseTouched' | 'images'>): number {
  const caseHeld = !!s.caseError || touchedProblems(caseProblems(s.caseForm), s.caseTouched).length > 0;
  const images = (s.images ?? []).filter((r) => r.error || (isEditable(r) && touchedProblems(wireOf(r.draft).problems.filter((p) => !CASE_FIELDS.includes(p.field)), r.touched).length > 0));
  return (caseHeld ? 1 : 0) + images.length;
}

export type StudyEditor = ReturnType<typeof createStudyEditor>;

export function createStudyEditor(studyId: string, deps: { refresh?: (studyUid: string | undefined) => Promise<void> } = {}) {
  const refresh = deps.refresh ?? ((studyUid) => refreshDetailsViews(queryClient, ENTERPRISE_CATALOGUE, studyUid));
  /** The study's UID, from its head: the DICOMweb reads to refresh are this study's. */
  let studyUid: string | undefined;
  /** The next case save may send the accession: a field lost focus, Done, or leaving. Keystroke saves may not. */
  let accessionReleased = false;

  return createStore<EditorState>((set, get) => {
    const patchRow = (imageId: string, p: Partial<ImageRow> | ((r: ImageRow) => Partial<ImageRow>)) =>
      set((s) => ({ images: s.images?.map((r) => (r.imageId === imageId ? { ...r, ...(typeof p === 'function' ? p(r) : p) } : r)) ?? null }));
    const caseOwed = () => Object.keys(caseDelta(get().caseForm, get().caseSaved)).length > 0;

    const saveCase = async (): Promise<boolean> => {
      const s = get();
      const change = caseChanges(s.caseForm, s.caseSaved, { accession: accessionReleased });
      accessionReleased = false;
      if (!Object.keys(change.body).length) {
        if (s.caseError && !caseProblems(s.caseForm).length) set({ caseError: null });
        return false;
      }
      try {
        await withRetry(() => patchStudyDetails(studyId, change.body), { attempts: CALL_ATTEMPTS });
        set((now) => ({
          caseSaved: { study: { ...now.caseSaved.study, ...change.groups }, diagnosis: 'diagnosis' in change ? change.diagnosis ?? null : now.caseSaved.diagnosis },
          caseError: null,
          saved: true,
        }));
        return true;
      } catch (err) {
        set({ caseError: refusalOf(err, 'study') });
        return false;
      }
    };

    const saveImage = async (imageId: string): Promise<boolean> => {
      const r = get().images?.find((x) => x.imageId === imageId);
      if (!r || !isEditable(r)) return false;
      const groups = deltaOf({ image: wireOf(r.draft).wire.image, study: {} }, { image: r.saved, study: {} }).image;
      if (!Object.keys(groups).length) {
        if (r.error) patchRow(imageId, { error: null });
        return false;
      }
      try {
        const v = await withRetry(() => patchImage(imageId, imageBody(groups)), { attempts: CALL_ATTEMPTS });
        patchRow(imageId, (now) => ({ saved: { ...now.saved, ...groups }, error: null, stage: v.stage, percent: v.percent ?? null, stageError: v.error }));
        set({ saved: true });
        return true;
      } catch (err) {
        patchRow(imageId, { error: refusalOf(err, 'image') });
        return false;
      }
    };

    const runtime = createDetailsEditorRuntime<EnterpriseImage, ImageRow>({
      save: (key) => (key === CASE ? saveCase() : saveImage(key)),
      refresh: () => refresh(studyUid),
      onPending: (pending) => set({ pending }),
      fetchImages: async () => (await fetchStudyImages(studyId)) as EnterpriseImage[],
      onImages: (images) => get().sync(images),
      onImagesError: (err) => {
        if (err instanceof UploadApiError && err.status === 404) {
          set({ imagesError: 'This study no longer exists. It may have been deleted.' });
          return true;
        }
        if (!get().images) set({ imagesError: 'Could not reach the server. Trying again…' });
        return false;
      },
      rows: () => get().images,
      isProcessing,
      delayMs: SAVE_DEBOUNCE_MS,
      pollMs: POLL_MS,
    });

    return {
      studyId,
      caseForm: { draft: EMPTY_DRAFT, diagnosis: '' },
      caseSaved: { study: {}, diagnosis: null },
      caseError: null,
      caseTouched: [],
      images: null,
      imagesError: null,
      selectedId: null,
      pending: false,
      saved: false,
      moved: [],
      open: (head) => {
        studyUid = head.study_instance_uid;
        const form = caseFormOf(head);
        set({ caseForm: form, caseSaved: caseSavedOf(form) });
        void runtime.load();
      },
      takeHead: (head) => {
        const s = get();
        const theirs = caseSavedOf(caseFormOf(head));
        const now = wireOf(s.caseForm.draft).wire.study;
        let draft = s.caseForm.draft;
        let diagnosis = s.caseForm.diagnosis;
        const saved: CaseSaved = { study: { ...s.caseSaved.study }, diagnosis: s.caseSaved.diagnosis };
        let changed = false;
        // A group is taken only where the form still shows what the server last took.
        for (const g of Object.keys(theirs.study) as (keyof Wire['study'])[]) {
          if (!(g in now) || !same(now[g], s.caseSaved.study[g]) || same(s.caseSaved.study[g], theirs.study[g])) continue;
          draft = { ...draft, ...studyGroupFields(g, theirs.study[g]) };
          saved.study[g] = theirs.study[g];
          changed = true;
        }
        if ((diagnosis.trim() || null) === s.caseSaved.diagnosis && s.caseSaved.diagnosis !== theirs.diagnosis) {
          diagnosis = theirs.diagnosis ?? '';
          saved.diagnosis = theirs.diagnosis;
          changed = true;
        }
        if (changed) set({ caseForm: { draft, diagnosis }, caseSaved: saved });
      },
      setCase: (p) => {
        set((s) => ({
          caseForm: { ...s.caseForm, draft: { ...s.caseForm.draft, ...p } },
          caseTouched: [...new Set([...s.caseTouched, ...(Object.keys(p) as (keyof MetadataDraft)[])])],
        }));
        // An accession being typed waits for its field to lose focus (flush).
        if (Object.keys(p).every((k) => ACCESSION_FIELDS.includes(k as keyof MetadataDraft))) return;
        runtime.schedule(CASE);
      },
      setDiagnosis: (v) => {
        set((s) => ({ caseForm: { ...s.caseForm, diagnosis: v } }));
        runtime.schedule(CASE);
      },
      setImage: (imageId, p) => {
        const r = get().images?.find((x) => x.imageId === imageId);
        if (!r || !isEditable(r)) return;
        patchRow(imageId, (now) => ({ draft: { ...now.draft, ...p }, touched: [...new Set([...now.touched, ...(Object.keys(p) as (keyof MetadataDraft)[])])] }));
        runtime.schedule(imageId);
      },
      select: (imageId) => set({ selectedId: imageId }),
      setMoveTo: (imageId, p) => patchRow(imageId, (r) => ({ moveTo: { ...r.moveTo, ...p }, moveError: null })),
      move: async (imageId) => {
        const r = get().images?.find((x) => x.imageId === imageId);
        if (!r || r.moving || !isEditable(r)) return;
        const accession = r.moveTo.accession.trim();
        const issuer = r.moveTo.issuer.trim() || null;
        if (!accession) {
          patchRow(imageId, { moveError: 'Give the accession number of the case to move it to.' });
          return;
        }
        // Its own details first, so what was typed goes with it.
        await runtime.flush(imageId);
        patchRow(imageId, { moving: true, moveError: null });
        try {
          const v = await withRetry(() => patchImageStudy(imageId, { accession_number: accession, accession_issuer: issuer }), { attempts: CALL_ATTEMPTS });
          if (v.study.public_id !== studyId) {
            set((s) => {
              const images = s.images?.filter((x) => x.imageId !== imageId) ?? null;
              return { images, selectedId: s.selectedId === imageId ? images?.[0]?.imageId ?? null : s.selectedId, moved: [...s.moved, { name: r.name, accession, studyId: v.study.public_id }], saved: true };
            });
          } else {
            // Alone in its study, the image kept it: the study took the accession.
            const group = { number: accession, issuer };
            patchRow(imageId, { moving: false, moveTo: { accession: '', issuer: '' } });
            set((s) => ({
              caseForm: { ...s.caseForm, draft: { ...s.caseForm.draft, ...studyGroupFields('accession', group) } },
              caseSaved: { ...s.caseSaved, study: { ...s.caseSaved.study, accession: group } },
              saved: true,
            }));
          }
          runtime.changed();
          // The case it went to changed too: its reads, by its UID (all studies' when that cannot be had).
          if (v.study.public_id !== studyId) void getStudyRef(v.study.public_id).then((ref) => refresh(ref.study_instance_uid), () => refresh(undefined));
        } catch (err) {
          patchRow(imageId, { moving: false, moveError: refusalOf(err, 'study').message });
        }
      },
      flush: async (key) => {
        if (key !== undefined) {
          if (key === CASE) accessionReleased = true;
          await runtime.flush(key);
          return;
        }
        // Done: everything, the accession included.
        accessionReleased = true;
        await Promise.all([runtime.flush(), caseOwed() ? runtime.flush(CASE) : undefined]);
      },
      sync: (list) => {
        const s = get();
        const byId = new Map((s.images ?? []).map((r) => [r.imageId, r]));
        const images = list.map((v) => {
          const r = byId.get(v.image_id);
          return r ? adoptImage(r, v) : rowOf(v);
        });
        const selectedId = images.some((r) => r.imageId === s.selectedId) ? s.selectedId : (images.find(isEditable) ?? images[0])?.imageId ?? null;
        set({ images, imagesError: null, selectedId });
      },
      close: () =>
        runtime.close(() => {
          // Leaving sends the accession too.
          accessionReleased = true;
          if (caseOwed()) void runtime.flush(CASE);
        }),
    };
  });
}
